
import { useState, useEffect, useRef } from 'react';
import { classifyConnectBankFailure } from './bankConnectionErrors';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CompletedRedirectPanel } from '@/components/shared/CompletedRedirectPanel';
import { LinkIcon, CheckIcon, Loader2, ShieldCheck, QrCode, ArrowLeft, Info } from 'lucide-react';
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { ASPSP } from '@/types/openpayments/bank';
import { BankAccountSummary } from '@/types/accounts';
import type { BankConstraintInfo } from '@/types/bankConstraints';
import { useNavigate } from 'react-router-dom';
import { ConsentAuthFlow } from '@/components/consent/ConsentAuthFlow';
import KycModal from '@/components/kyc/KycModal';
import { consumePendingConsentResume } from '@/lib/bankConsentResume';
import { captureError } from '@/lib/observability';
import { useTranslations } from 'use-intl';
import { BankAccountsToConnectTable, type UpdateAccessOutcome } from './BankAccountsToConnectTable';
import { AccountTypeCards } from './AccountTypeCards';
import type { AccountType } from '@/types/accounts';

const FALLBACK_COUNTRY_CODES = ['SE', 'NO', 'DK'] as const;

const PERMISSION_KEYS = [
  { title: 'accountInfo', body: 'accountInfoBody' },
  { title: 'balances', body: 'balancesBody' },
  { title: 'transactions', body: 'transactionsBody' },
] as const;

interface BankConnectionDialogProps {
  onConnect: (accounts: BankAccountSummary[]) => void;
  isOnboarding?: boolean;
}

/** The picked bank's logo + name, shown identically at the top of the authorize, consent and
 *  accounts steps — one definition rather than three copies that can drift apart. */
const SelectedBankHeader = ({ bank, className }: { bank: ASPSP | undefined; className?: string }) => {
  const t = useTranslations('consultantBank.connect');
  return (
    <div className={cn("flex items-center gap-3", className)}>
      {bank && (
        <div className="h-10 w-10 rounded-md border p-1 flex items-center justify-center bg-white">
          <img
            src={bank.logoUrl}
            alt={t('bankLogoAlt')}
            className="h-8 w-8 object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/placeholder.svg';
            }}
          />
        </div>
      )}
      <span className="font-semibold">{bank?.name || t('selectedBank')}</span>
    </div>
  );
};

const BankConnectionDialog = ({ onConnect, isOnboarding = false }: BankConnectionDialogProps) => {
  const navigate = useNavigate();
  const t = useTranslations('consultantBank');
  // The latest translator, so the loaders effects call word their messages without being rebuilt.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  // The SELECTION key, which is the provider-native ref (falling back to the bic where a
  // provider has none) — never the bic alone. Several institutions can share a bic (NDEASESS
  // carries Nordea, Nordea Corporate and Nordea First Card), so keying selection on the bic made
  // every lookup below resolve to whichever sorted first, including the name and logo the
  // confirmation screen shows. FIN-592.
  const [selectedBankKey, setSelectedBankKey] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string>('SE');
  const [availableCountries, setAvailableCountries] = useState<Array<{ code: string; name: string }>>([]);
  const [availableBanks, setAvailableBanks] = useState<ASPSP[]>([]);
  // Prefers the ref; falls back to a bic match so the interrupted-redirect resume path below
  // still resolves — it only stashes a bic, so it cannot disambiguate a shared one. Fixing that
  // properly means carrying the ref in the resume payload; tracked, not silently pretended.
  const selectedBank =
    availableBanks.find((bank) => (bank.ref ?? bank.bicFi) === selectedBankKey) ??
    availableBanks.find((bank) => bank.bicFi === selectedBankKey);
  // Presence, not truthiness (FIN-783): `selectedBank !== undefined`, never `!selectedBankId` --
  // a bic-less bank's bicFi is the legitimate empty-string sentinel, which every `!selectedBankId`
  // guard below used to treat identically to "nothing picked yet". `selectedBankId` itself stays
  // around only as the `bic` argument every downstream call actually needs.
  const hasSelectedBank = selectedBank !== undefined;
  const selectedBankId = selectedBank?.bicFi ?? null;
  // The ref/countryCode a bic-less bank is identified by (FIN-783) -- ref falls back to bicFi
  // for a bank with none, matching the picker's own onClick key (bank.ref ?? bank.bicFi).
  const selectedBankRef = selectedBank ? (selectedBank.ref ?? selectedBank.bicFi) : undefined;
  const selectedBankCountryCode = selectedBank?.countryCode ?? selectedCountry;
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStep, setConnectionStep] = useState<'select' | 'accountType' | 'personalExplainer' | 'authorize' | 'consent' | 'accounts' | 'complete'>('select');
  const [error, setError] = useState<string | null>(null);
  // FIN-1567 (AC1-AC4): null until the user picks at a dual-login bank; stays null at a
  // one-login bank, where the cards are never shown and the server default (business) applies.
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [discoveredAccounts, setDiscoveredAccounts] = useState<BankAccountSummary[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [isConnectingAccount, setIsConnectingAccount] = useState(false);
  const [showKycModal, setShowKycModal] = useState(false);
  const [kycCompanyId, setKycCompanyId] = useState<string | null>(null);
  const [bankConstraint, setBankConstraint] = useState<BankConstraintInfo | null>(null);
  // FIN-863 "Update access". consentReused: the list came from a consent the backend reused
  // without sending anyone to the bank, so the missing-accounts link is offered. updatingAccess:
  // the consent step is running a forced fresh authorization for that link.
  const [consentReused, setConsentReused] = useState(false);
  const [updatingAccess, setUpdatingAccess] = useState(false);
  const [updateOutcome, setUpdateOutcome] = useState<UpdateAccessOutcome>('none');
  const [isRelinking, setIsRelinking] = useState(false);
  // ConsentAuthFlow's completion effect can re-fire on a parent re-render; the relink writes
  // links and triggers syncs, so it must run once per attempt.
  const relinkInFlight = useRef(false);

  // Fetch available banks when dialog opens or country changes
  useEffect(() => {
    if (openDialog) {
      fetchSupportedCountries();
      fetchAvailableBanks(selectedCountry);
      // Reset error state when dialog opens
      setError(null);
    } else {
      // Reset state when dialog closes
      setSelectedBankKey(null);
      setSelectedCountry('SE');
      setConnectionStep('select');
      setError(null);
      setDiscoveredAccounts([]);
      setSelectedAccountId(null);
      setIsConnectingAccount(false);
      setBankConstraint(null);
      setConsentReused(false);
      setUpdatingAccess(false);
      setUpdateOutcome('none');
      setAccountType(null);
    }
  }, [openDialog]);

  // Fetch banks when country changes
  useEffect(() => {
    if (openDialog && selectedCountry) {
      fetchAvailableBanks(selectedCountry);
      // Reset selected bank when country changes
      setSelectedBankKey(null);
      setAccountType(null);
    }
  }, [selectedCountry]);

  // Resume a bank consent flow interrupted by a same-tab redirect to the bank's
  // sign-in page — the redirect wipes all React state, so we stash the consent
  // reference in sessionStorage before leaving and pick it back up here on mount.
  useEffect(() => {
    const pending = consumePendingConsentResume();
    if (!pending) return;

    (async () => {
      try {
        const status = await ServiceRegistry.getConsentService().getConsentInitiationStatus(
          pending.consentId,
          pending.authorisationId,
          pending.bic,
          pending.ref,
          pending.countryCode
        );

        if (!status.isCompleted) {
          toast.error(tRef.current('connect.toasts.authNotCompleted'));
          return;
        }

        const accountsResponse = await ServiceRegistry.getAccountService().getBankAccountsToConnect(
          pending.bic,
          pending.ref,
          pending.countryCode,
          pending.accountType
        );

        // The picker's own selection key is ref-first (falls back to bic) -- match it here so a
        // resumed bic-less bank (bic === '') is found again by selectedBank's own lookup.
        setSelectedBankKey(pending.ref ?? pending.bic);
        // FIN-1567: carried across the redirect (ConsentAuthFlow.savePendingConsentResume) so the
        // resumed connectSpecificAccount call below uses the login the user actually picked.
        setAccountType(pending.accountType ?? null);
        setDiscoveredAccounts(Array.isArray(accountsResponse) ? accountsResponse : []);
        setConnectionStep('accounts');
        setOpenDialog(true);
      } catch (error) {
        console.error("Error resuming bank consent flow:", error);
        toast.error(tRef.current('connect.toasts.resumeFailed'));
      }
    })();
  }, []);

  const fetchSupportedCountries = async () => {
    try {
      const bankInfoService = ServiceRegistry.getBankInfoService();
      const countries = await bankInfoService.getSupportedCountries();
      console.log("Fetched supported countries:", countries);
      setAvailableCountries(countries);
    } catch (error) {
      console.error("Error fetching supported countries:", error);
      // Set default countries as fallback
      setAvailableCountries(FALLBACK_COUNTRY_CODES.map((code) => ({ code, name: tRef.current(`countries.${code}`) })));
    }
  };

  const fetchAvailableBanks = async (countryCode: string) => {
    setIsLoading(true);
    setError(null);
    try {
      // Use the bank info service from the registry
      const bankInfoService = ServiceRegistry.getBankInfoService();
      const banks = await bankInfoService.listBanksByCountry(countryCode);
      console.log(`Fetched banks for ${countryCode}:`, banks);
      setAvailableBanks(banks);
    } catch (error) {
      console.error("Error fetching banks:", error);
      setError(tRef.current('connect.errors.loadBanks'));
      toast.error(tRef.current('connect.toasts.loadBanksFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // FIN-1567 AC3: the cards only appear when this bank's collapsed listing offers both logins --
  // a one-login bank (the common case today, and every AB's whole catalog) goes straight to
  // 'authorize' exactly as before this feature existed.
  const offersBothAccountTypes = (bank: ASPSP | undefined): boolean => {
    const types = bank?.psuTypes ?? [];
    return types.includes('business') && types.includes('personal');
  };

  const handleBankSelect = () => {
    // Only proceed if a bank is selected
    if (!hasSelectedBank) {
      toast.error(t('connect.toasts.selectBank'));
      return;
    }

    // Reset error state when proceeding to next step
    setError(null);
    setAccountType(null);
    setConnectionStep(offersBothAccountTypes(selectedBank) ? 'accountType' : 'authorize');
  };

  // FIN-1567 AC4: Personal shows the explanation step before the bank redirect; Business does not.
  const handleAccountTypeContinue = (type: AccountType) => {
    setAccountType(type);
    setConnectionStep(type === 'personal' ? 'personalExplainer' : 'authorize');
  };

  const connectBank = async () => {
    setIsConnecting(true);
    setError(null);
    
    if (!selectedBank) {
      setError(t('connect.errors.bankNotFound'));
      toast.error(t('connect.toasts.bankNotFound'));
      setIsConnecting(false);
      return;
    }
    
    try {
      console.log("Starting bank connection process for", selectedBank.name);
      await discoverAccountsAndAdvance(selectedBankId, selectedBankRef, selectedBankCountryCode, accountType ?? undefined);
      // Listing succeeded without a consent step, so the backend reused a valid consent.
      setConsentReused(true);
    } catch (error) {
      console.error("Error connecting to bank:", error);
      const failure = classifyConnectBankFailure(error);

      if (failure.kind === 'kyc') {
        // Onboarding must complete before a bank connection can proceed.
        setIsConnecting(false);
        setKycCompanyId(failure.companyId);
        setShowKycModal(true);
        return;
      }

      if (failure.kind === 'consent') {
        console.log("Consent required for bank:", selectedBank.name, "- triggering consent flow");
        setIsConnecting(false);
        const started = await startConsentFlowOrReportFailure();
        if (started) return;
      }

      setError(t('connect.errors.connectBank'));
      toast.error(t('connect.toasts.connectFailed'));
      // Don't change the step, allow user to retry
    } finally {
      setIsConnecting(false);
    }
  };

  /**
   * Fetches what this bank can offer and moves to the account-selection step. Both entry points
   * (connectBank, and the post-consent discovery below) did this identically -- an empty result
   * is not an error here, it just means every account is already connected or the consent covers
   * none, and the accounts step renders that state itself.
   */
  const discoverAccountsAndAdvance = async (bankId: string, ref?: string, countryCode?: string, forAccountType?: AccountType) => {
    const [accountsResponse, constraint] = await Promise.all([
      // ref/countryCode, not bankId alone (FIN-783): a bic-less bank's accounts can only be
      // found via its (country, ref) identity.
      ServiceRegistry.getAccountService().getBankAccountsToConnect(bankId, ref, countryCode, forAccountType),
      // bankConstraintsService still resolves by bic alone (accepted interim state, FIN-783 --
      // see the client-side equivalent) -- a bic-less bank simply has no constraint data.
      ServiceRegistry.getBankInfoService().getConstraint(bankId),
    ]);
    setBankConstraint(constraint);
    const accounts = Array.isArray(accountsResponse) ? accountsResponse : [];
    console.log(`Discovered ${accounts.length} account(s) available to connect`);
    setDiscoveredAccounts(accounts);
    setConnectionStep('accounts');
  };

  /** Returns whether the consent flow actually started; reports its own failure if not. */
  const startConsentFlowOrReportFailure = async (): Promise<boolean> => {
    try {
      await startConsentFlow();
      return true;
    } catch (consentError) {
      console.error("Error starting consent flow:", consentError);
      setError(t('connect.errors.consentStart'));
      toast.error(t('connect.toasts.consentStartFailed'));
      return false;
    }
  };

  // Start the consent creation flow
  const startConsentFlow = async () => {
    if (!hasSelectedBank) return;

    setError(null);
    setConnectionStep('consent');
  };

  // Discover accounts after consent is created
  const retryBankConnection = async () => {
    if (!hasSelectedBank) return;

    try {
      console.log("Discovering available accounts to connect");
      setIsConnecting(true);

      await discoverAccountsAndAdvance(selectedBankId, selectedBankRef, selectedBankCountryCode, accountType ?? undefined);
    } catch (error) {
      console.error("Error discovering accounts:", error);
      setError(t('connect.errors.discover'));
      toast.error(t('connect.toasts.discoverFailed'));
      setConnectionStep('authorize');
    } finally {
      setIsConnecting(false);
    }
  };

  const startUpdateAccess = () => {
    setError(null);
    setUpdateOutcome('none');
    setUpdatingAccess(true);
    setConnectionStep('consent');
  };

  /** The bank approved the fresh consent: move the connected accounts onto it, then re-list. */
  const finishUpdateAccess = async () => {
    if (!hasSelectedBank || relinkInFlight.current) return;
    relinkInFlight.current = true;
    setIsRelinking(true);
    try {
      await ServiceRegistry.getAccountService().relinkAccountsToLatestConsent(selectedBankId, selectedBankRef, selectedBankCountryCode);
    } catch (relinkError) {
      captureError(relinkError, 'BankConnectionDialog: update access failed (relink)');
      setUpdateOutcome('relinkFailed');
      setConnectionStep('accounts');
      setIsRelinking(false);
      relinkInFlight.current = false;
      return;
    }
    try {
      const accounts = await ServiceRegistry.getAccountService().getBankAccountsToConnect(selectedBankId, selectedBankRef, selectedBankCountryCode);
      const list = Array.isArray(accounts) ? accounts : [];
      setDiscoveredAccounts(list);
      setConsentReused(false);
      setUpdatingAccess(false);
      setUpdateOutcome(list.every((account) => account.alreadyConnected) ? 'noNewAccounts' : 'none');
    } catch (listError) {
      captureError(listError, 'BankConnectionDialog: update access failed (list)');
      setUpdateOutcome('listFailed');
    } finally {
      setConnectionStep('accounts');
      setIsRelinking(false);
      relinkInFlight.current = false;
    }
  };

  const failUpdateAccess = (reason: unknown) => {
    captureError(reason, 'BankConnectionDialog: update access failed (authorize)');
    setUpdateOutcome('accessNotUpdated');
    setConnectionStep('accounts');
  };

  // Connect a specific account
  const connectSpecificAccount = async (accountId: string) => {
    if (!hasSelectedBank) return;
    
    try {
      console.log("Connecting specific account:", accountId);
      setIsConnectingAccount(true);
      setSelectedAccountId(accountId);
      
      // Call the account service to connect the specific account
      const accountService = ServiceRegistry.getAccountService();
      const connectResult = await accountService.connectAccount(accountId, selectedBankId, selectedBankRef, selectedBankCountryCode, accountType ?? undefined);

      console.log("Successfully connected account:", connectResult.accountId);
      setConnectionStep('complete');

      // discoveredAccounts[].id is the provider's resourceId (from getBankAccountsToConnect),
      // not our persisted bank_accounts.id — using it downstream (e.g. sync-status polling)
      // fails with "invalid input syntax for type uuid". connectResult.accountId is the real
      // DB id; keep the discovered entry's display-only fields (balance/logoUrl/bic) alongside it.
      const discoveredAccount = discoveredAccounts.find(acc => acc.id === accountId);
      const accountsToReturn: BankAccountSummary[] = discoveredAccount
        ? [{
            ...discoveredAccount,
            id: connectResult.accountId,
            bankName: connectResult.bankName,
            accountNumber: connectResult.iban || discoveredAccount.accountNumber,
            maskedNumber: connectResult.maskedNumber ?? discoveredAccount.maskedNumber,
            currency: connectResult.currency,
          }]
        : [];
      
      setTimeout(() => {
        onConnect(accountsToReturn);
        setOpenDialog(false);
        toast.success(t('connect.toasts.connected'));
        setSelectedBankKey(null);
        setConnectionStep('select');
        
        if (isOnboarding) {
          navigate('/overview');
        }
      }, 1500);
      
    } catch (error) {
      console.error("Error connecting specific account:", error);
      setError(t('connect.errors.connectAccount'));
      toast.error(t('connect.toasts.accountFailed'));
      setSelectedAccountId(null);
    } finally {
      setIsConnectingAccount(false);
    }
  };
  
  const connectableCount = discoveredAccounts.filter((account) => !account.alreadyConnected).length;
  const connectableSummary = connectableCount > 0
    ? t('connect.foundAccounts', { count: connectableCount })
    : t('connect.noNewAccounts');

  // Render the appropriate step content
  const renderStepContent = () => {
    switch (connectionStep) {
      case 'select':
        return (
          <div className="space-y-4 py-4">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md border border-red-200 mb-4">
                {error}
              </div>
            )}

            {/* Country Picker */}
            <div className="space-y-2">
              <label htmlFor="country-select" className="text-sm font-medium">
                {t('connect.selectCountry')}
              </label>
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger id="country-select" className="w-full">
                  <SelectValue placeholder={t('connect.selectCountryPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {availableCountries.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="max-h-[300px] overflow-y-auto">
              {isLoading ? (
                <div className="flex justify-center items-center h-40">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('connect.bankColumn')}</TableHead>
                      <TableHead className="w-[100px]">{t('connect.selectColumn')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {availableBanks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center h-40">
                          {t('connect.noBanks')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      availableBanks.map((bank) => (
                        <TableRow 
                          key={bank.ref ?? bank.bicFi}
                          className={cn(
                            "cursor-pointer",
                            selectedBankKey === (bank.ref ?? bank.bicFi) && "bg-muted"
                          )}
                          onClick={() => setSelectedBankKey(bank.ref ?? bank.bicFi)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-md border p-1 flex items-center justify-center bg-white">
                                <img 
                                  src={bank.logoUrl} 
                                  alt={t('connect.namedLogoAlt', { name: bank.name })}
                                  className="h-8 w-8 object-contain" 
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = '/placeholder.svg';
                                  }} 
                                />
                              </div>
                              <span className="font-medium">{bank.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {selectedBankKey === (bank.ref ?? bank.bicFi) && (
                              <CheckIcon className="h-5 w-5 text-primary mx-auto" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
            <Button
              onClick={handleBankSelect}
              className="w-full bg-primary hover:bg-primary/90"
              disabled={isLoading || !hasSelectedBank}
            >
              {t('connect.continue')}
            </Button>
          </div>
        );

      case 'accountType':
        return (
          <div className="space-y-4">
            <AccountTypeCards
              bankName={selectedBank?.name ?? t('connect.thisBank')}
              onContinue={handleAccountTypeContinue}
            />
            <Button variant="outline" className="w-full" onClick={() => setConnectionStep('select')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('connect.back')}
            </Button>
          </div>
        );

      case 'personalExplainer':
        return (
          <div className="space-y-4 py-4">
            <div className="bg-muted p-4 rounded-lg space-y-3">
              <div className="flex items-start gap-2">
                <Info className="h-5 w-5 text-primary mt-0.5" />
                <p className="text-sm">{t('connect.personalExplainer.body')}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setConnectionStep('accountType')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('connect.back')}
              </Button>
              <Button className="flex-1" onClick={() => setConnectionStep('authorize')} data-testid="personal-explainer-continue">
                {t('connect.continue')}
              </Button>
            </div>
          </div>
        );

      case 'authorize':
        return (
          <div className="space-y-4 py-4">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md border border-red-200 mb-4">
                {error}
              </div>
            )}
            <div className="bg-muted p-4 rounded-lg">
              <SelectedBankHeader bank={selectedBank} className="mb-4" />
              
              <div className="space-y-3 mb-4">
                {PERMISSION_KEYS.map(({ title, body }) => (
                  <div key={title} className="flex items-start gap-2">
                    <ShieldCheck className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium">{t(`connect.permissions.${title}`)}</p>
                      <p className="text-sm text-muted-foreground">{t(`connect.permissions.${body}`)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-sm">
                {t('connect.authorizeNotice', { bank: selectedBank?.name || t('connect.fallbackBank') })}
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={() => setConnectionStep('select')}
                disabled={isConnecting}
              >
                {t('connect.back')}
              </Button>
              <Button 
                onClick={connectBank} 
                className="flex-1" 
                disabled={isConnecting}
              >
                {isConnecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('connect.authorizing')}
                  </>
                ) : (
                  t('connect.authorize')
                )}
              </Button>
            </div>
          </div>
        );
      
      case 'consent':
        return (
          <div className="space-y-4 py-4">
            {/* Bank Header */}
            <div className="bg-muted p-4 rounded-lg">
              <SelectedBankHeader bank={selectedBank} className="mb-2" />
              <p className="text-sm text-muted-foreground">
                {t('connect.creatingConsent')}
              </p>
            </div>

            {/* Use ConsentAuthFlow component */}
            {selectedBank && (
              <ConsentAuthFlow
                bic={selectedBank.bicFi}
                // The picker tracks selection by bicFi, so recover this institution's own ref
                // from the list it was chosen from (FIN-592). countryCode identifies a bic-less
                // bank, whose bicFi is the empty-string sentinel (FIN-783).
                bankRef={selectedBankRef}
                countryCode={selectedBankCountryCode}
                forceNew={updatingAccess}
                accountType={accountType ?? undefined}
                onConsentCompleted={async (consentId, { isExisting }) => {
                  console.log("Consent completed:", consentId);
                  if (updatingAccess) {
                    await finishUpdateAccess();
                    return;
                  }
                  setConsentReused(isExisting);
                  await retryBankConnection();
                }}
                onError={(error) => {
                  if (updatingAccess) {
                    failUpdateAccess(error);
                    return;
                  }
                  console.error("Consent auth error:", error);
                  setError(t('connect.errors.createConsent'));
                  toast.error(t('connect.toasts.consentFailed'));
                  setConnectionStep('authorize');
                }}
              />
            )}

            {/* Back button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                // Leaving an update returns to the list it started from, untouched.
                if (updatingAccess) {
                  setUpdatingAccess(false);
                  setConnectionStep('accounts');
                  return;
                }
                setConnectionStep('authorize');
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('connect.back')}
            </Button>
          </div>
        );

      case 'accounts':
        return (
          <div className="space-y-4 py-4">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md border border-red-200 mb-4">
                {error}
              </div>
            )}
            
            {/* Bank Header */}
            <div className="bg-muted p-4 rounded-lg">
              <SelectedBankHeader bank={selectedBank} className="mb-2" />
              <p className="text-sm text-muted-foreground">
                {connectableSummary}
              </p>
            </div>

            {discoveredAccounts.length > 0 && bankConstraint?.aisMaxDateRangeMonths != null && (
              <Alert data-testid="ais-constraint-note">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  {t('connect.constraintNote', { months: bankConstraint.aisMaxDateRangeMonths })}
                </AlertDescription>
              </Alert>
            )}

            <BankAccountsToConnectTable
              accounts={discoveredAccounts}
              connectingAccountId={selectedAccountId}
              isConnectingAccount={isConnectingAccount}
              onConnect={connectSpecificAccount}
              showUpdateAccessLink={consentReused}
              onUpdateAccess={startUpdateAccess}
              updateOutcome={updateOutcome}
              onRetryUpdate={startUpdateAccess}
              onRetryRelink={finishUpdateAccess}
              isRetryingRelink={isRelinking}
            />

            {/* Back button */}
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => {
                // A different bank starts clean: no leftover update mode or outcome.
                setUpdatingAccess(false);
                setUpdateOutcome('none');
                setConsentReused(false);
                setConnectionStep('select');
              }}
              disabled={isConnectingAccount}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('connect.backToBanks')}
            </Button>
          </div>
        );

      case 'complete':
        return (
          <CompletedRedirectPanel
            title={t('connect.successTitle')}
            body={t('connect.successBody')}
            progressNote={t('connect.redirecting')}
          />
        );
    }
  };

  return (
    <>
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogTrigger asChild>
          <Button className="flex items-center gap-2 bg-primary text-white hover:bg-primary/90">
            <LinkIcon size={16} />
            {t('connect.button')}
          </Button>
        </DialogTrigger>
        <DialogContent className="glass-card max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-primary text-xl">
              {t(`connect.titles.${connectionStep}`)}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {t(`connect.descriptions.${connectionStep}`)}
            </DialogDescription>
          </DialogHeader>
          {renderStepContent()}
        </DialogContent>
      </Dialog>

      {/* ZTL KYC onboarding — shown when bank connection requires onboarding to be completed first */}
      {showKycModal && kycCompanyId && (
        <KycModal
          open={showKycModal}
          onOpenChange={(open) => {
            setShowKycModal(open);
            if (!open) setKycCompanyId(null);
          }}
          companyId={kycCompanyId}
          provider="ztl"
          kycType="onboarding"
          onKycCompleted={() => {
            setShowKycModal(false);
            setKycCompanyId(null);
            // Retry bank connection now that KYC is complete
            connectBank();
          }}
        />
      )}
    </>
  );
};

export default BankConnectionDialog;
